// Endpoint definitions injected in outgoingShipmentsApi.js; DTO → VM runs in transformResponse.
import { TAG_TYPES } from '@/lib/api/tags';
import { processServerError, handledError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import { processFulfilledAllocationIds } from '@/lib/mappers/fulfill';
import {
  processShipmentForFulfill,
  buildFulfillShipmentDto,
} from '../mappers/fulfillShipmentMappers';
import { buildOutgoingShipmentFiltersInput } from '@/lib/mappers/filters';
import {
  buildOutgoingShipmentsQuery,
  processOutgoingShipmentsPage,
  processOutgoingShipmentFilterOptions,
  processOutgoingShipmentDetail,
  processShipmentLinkedOrder,
  buildLinkedOrderOrderBy,
  processShipmentAllocations,
  processOutgoingShipmentForm,
  buildOutgoingShipmentDto,
} from '../mappers/outgoingShipmentsMappers';
import { buildOrderShipmentDto } from '@/lib/mappers/orderShipments';

// The writes are a sub-resource of pending orders; the list is GraphQL.
const PATHS = {
  list: (module) => `/${module}/pending-orders/shipments`,
  detail: (module, id) => `/${module}/pending-orders/shipments/${id}`,
  orderShipments: (module, orderId) => `/${module}/pending-orders/${orderId}/shipments`,
  fulfill: (module, id) => `/${module}/pending-orders/shipments/${id}/fulfill`,
};

// The filter panel's Material and Customer choices — only what the table's shipments carry.
export const OUTGOING_SHIPMENT_FILTER_OPTIONS_QUERY = `
  query OutgoingShipmentFilterOptions($certificateType: CertificateType, $filters: OutgoingShipmentFilters) {
    outgoingShipmentFilterOptions(certificateType: $certificateType, filters: $filters) {
      materials { id name }
      customers { id name }
    }
  }
`;

// The fields the Outgoing Shipments table renders; `OutgoingShipment` has 40+.
export const OUTGOING_SHIPMENTS_QUERY = `
  query OutgoingShipments($certificateType: CertificateType, $limit: Int, $page: Int, $filters: OutgoingShipmentFilters, $search: OutgoingShipmentSearch, $orderBy: [OutgoingShipmentOrderBy!]) {
    outgoingShipments(certificateType: $certificateType, limit: $limit, page: $page, filters: $filters, search: $search, orderBy: $orderBy) {
      nodes {
        id
        shipmentNo
        status
        shipmentStatus
        material { id name }
        site { id siteName }
        backToBackAccount { accountName }
        customerAccount { id companyname }
        shippingDetails { shippingFrom shippingTo }
        terminal
        terminalRefId
        vesselName
        workingUnit
        declarationUnit
        totalQuantityWorking
        totalQuantityDeclaration
        allocatedQuantityWorking
        allocatedQuantityDeclaration
        remainingToAllocateQuantityWorking
        remainingToAllocateQuantityDeclaration
        unassignedQuantityWorking
        unassignedQuantityDeclaration
        linkedOrders { id }
        dispatchDate
        discrepancyCount
      }
      pageInfo {
        total
        page
        limit
      }
    }
  }
`;

// Detail page header + "Outgoing shipment details" tab — outgoingShipment(id) takes no certificateType, it is account-scoped only.
export const OUTGOING_SHIPMENT_DETAIL_QUERY = `
  query OutgoingShipment($id: ID!) {
    outgoingShipment(id: $id) {
      id
      shipmentNo
      status
      shipmentStatus
      material { id name }
      site { id siteName }
      backToBackAccountId
      backToBackAccount { accountName }
      terminalRefId
      customerAccount { id companyname }
      shippingDetails { shippingFrom shippingTo }
      terminal
      vesselName
      workingUnit
      declarationUnit
      totalQuantityWorking
      totalQuantityDeclaration
      allocatedQuantityWorking
      allocatedQuantityDeclaration
      remainingToAllocateQuantityWorking
      remainingToAllocateQuantityDeclaration
      unassignedQuantityWorking
      unassignedQuantityDeclaration
      avgGhgSaving
      dispatchDate
      discrepancyCount
    }
  }
`;

// Linked orders tab — the whole linked list, as the inbound detail tabs read theirs.
export const SHIPMENT_LINKED_ORDERS_QUERY = `
  query LinkedOrdersForOutgoingShipment($shipmentId: ID!, $orderBy: [PendingOrderOrderBy!]) {
    linkedOrdersForOutgoingShipment(shipmentId: $shipmentId, limit: 100, page: 1, orderBy: $orderBy) {
      nodes {
        assignedQuantityWorking
        assignedQuantityDeclaration
        workingUnit
        declarationUnit
        order {
          id
          orderNo
          status
          material { id name }
          site { id siteName }
          terminal
          terminalRefId
          customerAccount { id companyname }
          shippingDetails { id shippingTo }
          contractedWorkingQuantity
          contractedDeclarationQuantity
          shippedWorkingQuantity
          shippedDeclarationQuantity
          remainingToShipWorkingQuantity
          remainingToShipDeclarationQuantity
          allocatedWorkingQuantity
          allocatedDeclarationQuantity
          remainingToAllocateWorkingQuantity
          remainingToAllocateDeclarationQuantity
          tolerance
          ghgEmission
          ghgSaving
          avgGhgSaving
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          dispatchDate
        }
      }
      pageInfo {
        total
        isEmpty
      }
    }
  }
`;

// Allocation details tab — `limit: 100` is the server's max, so one page holds all.
export const SHIPMENT_ALLOCATIONS_QUERY = `
  query AllocationDetailsForOutgoingShipment($shipmentId: ID!) {
    allocationDetailsForOutgoingShipment(shipmentId: $shipmentId, limit: 100, page: 1) {
      nodes {
        id
        transactionId
        approvalStatus
        canGenerateDeclaration
        canRevoke
        sentFrom
        receivedAt
        shippingTo
        shippingToSites
        transactionDate
        workingUnit
        declarationUnit
        certifiedQuantityWorking
        certifiedQuantityDeclaration
        massBalancePeriodLabel
        reference { orderNo shipmentNo }
        associatedDeclarationNo
        transactedBy { name email }
        countryOfOrigin
        rawMaterialType
        ghgEmission
        ghgEmissionSavings
        feedstockCategory
        allocatedQuota
        dateOfDispatch
        daysLeft
      }
    }
  }
`;

export const outgoingShipmentsEndpoints = (build) => ({
  getOutgoingShipments: build.query({
    // `document` routes this through GraphQL; the module scopes it as `certificateType`.
    query: (screenState) => ({
      document: OUTGOING_SHIPMENTS_QUERY,
      variables: buildOutgoingShipmentsQuery(screenState),
    }),
    transformResponse: (data) => processOutgoingShipmentsPage(data.outgoingShipments),
    providesTags: (result) =>
      result
        ? [
            ...result.rows.map((row) => ({ type: TAG_TYPES.OutgoingShipment, id: row.id })),
            { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
          ]
        : [{ type: TAG_TYPES.OutgoingShipment, id: 'LIST' }],
  }),

  // Takes the page's fixed scope only — a picked filter here would hide its siblings.
  getOutgoingShipmentFilterOptions: build.query({
    query: ({ certificateType, extraFilters }) => ({
      document: OUTGOING_SHIPMENT_FILTER_OPTIONS_QUERY,
      variables: {
        certificateType,
        filters: buildOutgoingShipmentFiltersInput({}, extraFilters),
      },
    }),
    transformResponse: (data) =>
      processOutgoingShipmentFilterOptions(data.outgoingShipmentFilterOptions),
    providesTags: [{ type: TAG_TYPES.OutgoingShipment, id: 'LIST' }],
  }),

  getOutgoingShipmentDetail: build.query({
    query: ({ id }) => ({ document: OUTGOING_SHIPMENT_DETAIL_QUERY, variables: { id } }),
    transformResponse: (data) => processOutgoingShipmentDetail(data.outgoingShipment),
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.OutgoingShipment, id }],
  }),

  getShipmentForFulfill: build.query({
    query: ({ id }) => ({ document: OUTGOING_SHIPMENT_DETAIL_QUERY, variables: { id } }),
    transformResponse: (data) => processShipmentForFulfill(data.outgoingShipment),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.OutgoingShipment, id }],
  }),

  getShipmentLinkedOrders: build.query({
    query: ({ id, sort }) => ({
      document: SHIPMENT_LINKED_ORDERS_QUERY,
      variables: { shipmentId: id, orderBy: buildLinkedOrderOrderBy(sort) },
    }),
    transformResponse: (data) =>
      (data?.linkedOrdersForOutgoingShipment?.nodes ?? []).map(processShipmentLinkedOrder),
    providesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Order, id: `SHIPMENT-${id}` },
      // Deleting an order unlinks it without knowing which shipments held it.
      { type: TAG_TYPES.Order, id: 'LIST' },
    ],
  }),

  getShipmentAllocations: build.query({
    query: ({ id }) => ({ document: SHIPMENT_ALLOCATIONS_QUERY, variables: { shipmentId: id } }),
    transformResponse: (data) =>
      processShipmentAllocations(data.allocationDetailsForOutgoingShipment),
    providesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Allocation, id: `SHIPMENT-${id}` },
      // Revoke and generate-declaration know the allocation, never which shipment holds it.
      { type: TAG_TYPES.Allocation, id: 'LIST' },
    ],
  }),

  // Update shipment ▸ creates a NEW outgoing shipment carrying the order's own details.
  createShipmentForOrder: build.mutation({
    query: ({ module, orderId, values }) => ({
      url: PATHS.orderShipments(module, orderId),
      method: 'POST',
      body: buildOrderShipmentDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('OUTGOING_SHIPMENTS_SAVED_FLASH'),
    invalidatesTags: (result, error, { shipmentId }) => [
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
      { type: TAG_TYPES.Order, id: `SHIPMENT-${shipmentId}` },
      { type: TAG_TYPES.Order, id: 'LIST' },
    ],
  }),

  // JSON, not multipart — the outgoing-shipment form has no attachments.
  createOutgoingShipment: build.mutation({
    query: ({ module, values }) => ({
      url: PATHS.list(module),
      method: 'POST',
      body: buildOutgoingShipmentDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('OUTGOING_SHIPMENTS_SAVED_FLASH'),
    invalidatesTags: [{ type: TAG_TYPES.OutgoingShipment, id: 'LIST' }],
  }),

  // Edit's prefill; also carries what Edit is allowed to change.
  getOutgoingShipmentForm: build.query({
    query: ({ module, id }) => ({ url: PATHS.detail(module, id) }),
    transformResponse: (dto) => processOutgoingShipmentForm(dto.output?.data ?? {}),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.OutgoingShipment, id }],
  }),

  // PUT, not PATCH — that is the route the server exposes.
  updateOutgoingShipment: build.mutation({
    query: ({ module, id, values }) => ({
      url: PATHS.detail(module, id),
      method: 'PUT',
      body: buildOutgoingShipmentDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('OUTGOING_SHIPMENTS_UPDATED'),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.OutgoingShipment, id },
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
    ],
  }),

  // Fulfill request — links the picked order and allocates the chosen inbounds in one call.
  fulfillOutgoingShipment: build.mutation({
    query: ({ module, id, values }) => ({
      url: PATHS.fulfill(module, id),
      method: 'POST',
      body: buildFulfillShipmentDto(values),
    }),
    transformResponse: (dto) => processFulfilledAllocationIds(dto?.output?.data),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('OUTGOING_SHIPMENTS_FULFILLED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.OutgoingShipment, id: arg.id },
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
      // A fulfil links the order it was pointed at and allocates against it.
      { type: TAG_TYPES.Order, id: `SHIPMENT-${arg.id}` },
      { type: TAG_TYPES.Order, id: 'LIST' },
      { type: TAG_TYPES.Allocation, id: `SHIPMENT-${arg.id}` },
      { type: TAG_TYPES.Allocation, id: 'LIST' },
    ],
  }),

  // The server 400s on a shipment with allocations; linked orders it unlinks itself.
  deleteOutgoingShipment: build.mutation({
    query: ({ module, id }) => ({
      url: PATHS.detail(module, id),
      method: 'DELETE',
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('OUTGOING_SHIPMENTS_DELETED'),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.OutgoingShipment, id },
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
    ],
  }),
});
